import { useEffect, useState } from 'react'
import { motion, useMotionValue, useTransform } from 'framer-motion'
import ParticleBackground from '../animations/ParticleBackground'
import CharacterReveal from '../animations/CharacterReveal'
import TypingEffect from '../animations/TypingEffect'

const roles = ['Developer', 'Designer', 'Freelancer', 'Innovator']

// Apple-style motion defaults
const transition = { duration: 0.6, ease: [0.25, 1, 0.5, 1] }
const springConfig = { type: "spring", stiffness: 60, damping: 10 }

export default function Hero() {
  const [isVisible, setIsVisible] = useState(false)
  const mouseX = useMotionValue(0)
  const mouseY = useMotionValue(0)

  useEffect(() => {
    setIsVisible(true)
  }, [])

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      mouseX.set(e.clientX / window.innerWidth)
      mouseY.set(e.clientY / window.innerHeight)
    }
    window.addEventListener('mousemove', handleMouseMove)
    return () => window.removeEventListener('mousemove', handleMouseMove)
  }, [mouseX, mouseY])

  const rotateX = useTransform(mouseY, [0, 1], [5, -5])
  const rotateY = useTransform(mouseX, [0, 1], [-5, 5])

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition,
    },
  }

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-background py-20"
    >
      {/* Subtle particle background */}
      <ParticleBackground />
      
      {/* Floating gradient orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <motion.div
              className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full blur-3xl opacity-20"
              style={{
                background: 'linear-gradient(135deg, #0066CC, #0066CC)', // Science Blue
              }}
          animate={{
            x: [0, 50, 0],
            y: [0, 30, 0],
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
            <motion.div
              className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full blur-3xl opacity-15"
              style={{
                background: 'linear-gradient(135deg, #0066CC, #0066CC)', // Science Blue
              }}
          animate={{
            x: [0, -50, 0],
            y: [0, -30, 0],
            scale: [1, 1.15, 1],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
      </div>
      
      <motion.div
        className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-20"
        variants={containerVariants}
        initial="hidden"
        animate={isVisible ? 'visible' : 'hidden'}
      >
        <div className="text-center">
          <motion.div
            variants={itemVariants}
            className="mb-8"
          >
            <motion.span
              className="inline-block px-4 py-2 text-xs font-medium text-accent uppercase tracking-widest mb-6"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              transition={springConfig}
            >
              Welcome to My Portfolio
            </motion.span>
          </motion.div>

                <motion.h1
                  variants={itemVariants}
                  className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-semibold mb-6 font-display leading-[1.1] tracking-tight"
                  style={{
                    rotateX,
                    rotateY,
                    transformStyle: 'preserve-3d',
                  }}
                >
                  <div className="block text-text-primary mb-3 text-3xl sm:text-4xl md:text-5xl font-normal">
                    <CharacterReveal text="Hey, I'm" delay={0.2} />
                  </div>
                  <div className="block text-gradient-glow">
                    <CharacterReveal text="Peyton Clark" delay={0.5} />
                  </div>
                </motion.h1>

          <motion.div
            variants={itemVariants}
            className="h-16 sm:h-20 mb-10 flex items-center justify-center"
          >
            <div className="text-3xl sm:text-4xl md:text-5xl font-semibold text-text-primary tracking-tight">
              <TypingEffect words={roles} typingSpeed={100} pauseDuration={2000} />
            </div>
          </motion.div>

          <motion.p
            variants={itemVariants}
            className="text-base sm:text-lg text-text-primary/70 max-w-xl mx-auto mb-12 leading-relaxed font-normal"
          >
            Crafting experiences that merge function and emotion.
            <br />
            <span className="text-text-primary/60">Let's build something amazing together.</span>
          </motion.p>

          <motion.div
            variants={itemVariants}
            className="flex flex-col sm:flex-row gap-6 justify-center items-center"
          >
            <motion.a
              href="#projects"
              data-magnetic
              className="px-8 py-3.5 neu rounded-2xl font-medium text-text-primary hover:text-accent transition-all text-base neu-hover"
              whileHover={{ 
                scale: 1.02,
                y: -1,
              }}
              whileTap={{ scale: 0.98 }}
              transition={springConfig}
              onClick={(e) => {
                e.preventDefault()
                document.querySelector('#projects')?.scrollIntoView({ behavior: 'smooth' })
              }}
            >
              View My Work
            </motion.a>
            <motion.a
              href="#contact"
              data-magnetic
              className="px-8 py-3.5 neu rounded-2xl font-medium text-text-primary hover:text-accent transition-all text-base neu-hover"
              whileHover={{ 
                scale: 1.02,
                y: -1,
              }}
              whileTap={{ scale: 0.98 }}
              transition={springConfig}
              onClick={(e) => {
                e.preventDefault()
                document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })
              }}
            >
              Get In Touch
            </motion.a>
          </motion.div>
        </div>
      </motion.div>

      {/* Scroll Indicator - Top */}
      <motion.div
        className="absolute top-8 left-1/2 -translate-x-1/2 z-20"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
          className="flex flex-col items-center text-text-muted"
        >
          <svg
            className="w-5 h-5 mb-2"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path d="M5 10l7-7m0 0l7 7m-7-7v18" />
          </svg>
          <span className="text-xs uppercase tracking-wider">Scroll</span>
        </motion.div>
      </motion.div>
    </section>
  )
}
